import { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api, apiRoutes } from '../../../../api';
import { pointInPolygon, getCentroid, toGeometry } from './geoScopeUtils';

function useDebounce(val, ms) {
    const [dv, setDv] = useState(val);
    useEffect(() => { const t = setTimeout(() => setDv(val), ms); return () => clearTimeout(t); }, [val, ms]);
    return dv;
}

function FlyTo({ target }) {
    const map = useMap();
    useEffect(() => {
        if (!target) return;
        if (target.bbox) {
            const { min_lat, min_lon, max_lat, max_lon } = target.bbox;
            map.fitBounds([[min_lat, min_lon], [max_lat, max_lon]], { padding: [24, 24], animate: false });
        } else if (target.center) {
            map.setView([target.center.lat, target.center.lon], 13, { animate: false });
        }
    }, [target, map]);
    return null;
}

function NeighborhoodLayer({ neighborhoods, selectedIds, onToggle, drawMode }) {
    const style = useCallback((feature) => {
        const sel = selectedIds.has(feature.properties.id);
        return { color: sel ? '#3949ab' : '#78909c', weight: sel ? 2.5 : 1,
            fillColor: sel ? '#3949ab' : '#cfd8dc', fillOpacity: sel ? 0.4 : 0.12 };
    }, [selectedIds, drawMode]); // eslint-disable-line

    const onEachFeature = useCallback((feature, layer) => {
        layer.bindTooltip(feature.properties.name, { sticky: true, className: 's5-tooltip' });
        layer.on('click', (e) => { if (!drawMode) { L.DomEvent.stopPropagation(e); onToggle(feature.properties); } });
    }, [onToggle, drawMode]);

    const features = neighborhoods.map(n => {
        const geom = toGeometry(n.boundary);
        return geom ? { type: 'Feature', properties: { id: n.id, name: n.name }, geometry: geom } : null;
    }).filter(Boolean);
    if (!features.length) return null;

    return (
        <GeoJSON
            key={neighborhoods.map(n => n.id).join(',') + [...selectedIds].join(',') + drawMode}
            data={{ type: 'FeatureCollection', features }}
            style={style}
            onEachFeature={onEachFeature}
        />
    );
}

function DrawLayer({ drawMode, onDrawComplete }) {
    const map = useMap();
    const drawing = useRef(false);
    const points = useRef([]);
    const polylineRef = useRef(null);

    useEffect(() => {
        if (drawMode) { map.dragging.disable(); map.getContainer().style.cursor = 'crosshair'; }
        else {
            map.dragging.enable(); map.getContainer().style.cursor = '';
            if (polylineRef.current) { map.removeLayer(polylineRef.current); polylineRef.current = null; }
            points.current = []; drawing.current = false;
        }
    }, [drawMode, map]);

    useMapEvents({
        mousedown(e) { if (!drawMode) return; drawing.current = true; points.current = [[e.latlng.lat, e.latlng.lng]]; },
        mousemove(e) {
            if (!drawMode || !drawing.current) return;
            points.current.push([e.latlng.lat, e.latlng.lng]);
            if (polylineRef.current) map.removeLayer(polylineRef.current);
            polylineRef.current = L.polyline(points.current, { color: '#6a1b9a', weight: 2.5, dashArray: '6 4', opacity: 0.85 }).addTo(map);
        },
        mouseup() {
            if (!drawMode || !drawing.current) return;
            drawing.current = false;
            if (polylineRef.current) { map.removeLayer(polylineRef.current); polylineRef.current = null; }
            const pts = points.current; points.current = [];
            if (pts.length > 3) {
                const flash = L.polygon([...pts, pts[0]], { color: '#6a1b9a', fillColor: '#6a1b9a', fillOpacity: 0.12, weight: 2 }).addTo(map);
                setTimeout(() => map.removeLayer(flash), 1200);
                onDrawComplete(pts);
            }
        },
    });
    return null;
}

/**
 * Props:
 *   selectedMap, setSelectedMap — shared Map<id, nbr>
 *   onNeighborhoodsLoaded(nbrs) — called when a city's neighborhoods load (for overlay map)
 */
export default function NeighborhoodsTab({ selectedMap, setSelectedMap, onNeighborhoodsLoaded, initialCityId }) {
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selProvince, setSelProvince] = useState('');
    const [selCity, setSelCity] = useState('');
    const [selRegion, setSelRegion] = useState('');
    const [cityNbrs, setCityNbrs] = useState([]);
    const [loadingNbrs, setLoadingNbrs] = useState(false);
    const [search, setSearch] = useState('');
    const dSearch = useDebounce(search, 250);
    const [flyTarget, setFlyTarget] = useState(null);
    const [drawMode, setDrawMode] = useState(false);
    const [lastDrawCount, setLastDrawCount] = useState(0);

    useEffect(() => { api.get(apiRoutes.locations.provinces).then(r => setProvinces(r.data)).catch(() => {}); }, []);

    // If returning to this step with already-selected neighborhoods, restore city dropdown
    useEffect(() => {
        if (!initialCityId || selCity) return;
        api.get(`/cities/${initialCityId}/`).then(r => {
            const provinceId = r.data?.province;
            if (provinceId) {
                setSelProvince(String(provinceId));
                // Load cities for this province, then set the city
                api.get(`/provinces/${provinceId}/cities/`).then(cr => {
                    setCities(cr.data);
                    setSelCity(String(initialCityId));
                }).catch(() => { setSelCity(String(initialCityId)); });
            } else {
                setSelCity(String(initialCityId));
            }
        }).catch(() => {});
    }, [initialCityId]); // eslint-disable-line

    useEffect(() => {
        if (!selProvince) { setCities([]); setSelCity(''); return; }
        // Don't reset selCity if it was just set by the restore effect
        api.get(`/provinces/${selProvince}/cities/`).then(r => setCities(r.data)).catch(() => {});
    }, [selProvince]); // eslint-disable-line

    useEffect(() => {
        if (!selCity) { setRegions([]); setSelRegion(''); setCityNbrs([]); if (onNeighborhoodsLoaded) onNeighborhoodsLoaded([]); return; }
        api.get(`/regions/?city=${selCity}`).then(r => setRegions(r.data)).catch(() => {});
        setSelRegion('');
        setLoadingNbrs(true);
        api.get(`/neighborhoods/?city=${selCity}&page_size=500`)
            .then(r => {
                const data = r.data?.results ?? r.data;
                setCityNbrs(data);
                if (onNeighborhoodsLoaded) onNeighborhoodsLoaded(data);
                let minLat = Infinity, minLon = Infinity, maxLat = -Infinity, maxLon = -Infinity;
                data.forEach(n => {
                    if (!n.bbox) return;
                    minLat = Math.min(minLat, n.bbox.min_lat); minLon = Math.min(minLon, n.bbox.min_lon);
                    maxLat = Math.max(maxLat, n.bbox.max_lat); maxLon = Math.max(maxLon, n.bbox.max_lon);
                });
                if (minLat !== Infinity) setFlyTarget({ bbox: { min_lat: minLat, min_lon: minLon, max_lat: maxLat, max_lon: maxLon } });
            })
            .catch(() => {}).finally(() => setLoadingNbrs(false));
    }, [selCity]); // eslint-disable-line

    useEffect(() => {
        if (!selRegion) return;
        const r = regions.find(r => String(r.id) === String(selRegion));
        if (r?.bbox) setFlyTarget({ bbox: r.bbox });
        else if (r?.center) setFlyTarget({ center: r.center });
    }, [selRegion, regions]);

    const toggle = useCallback((nbr) => {
        setSelectedMap(prev => {
            const next = new Map(prev);
            if (next.has(nbr.id)) next.delete(nbr.id);
            else { next.set(nbr.id, nbr); if (nbr.bbox || nbr.center) setFlyTarget(nbr); }
            return next;
        });
    }, [setSelectedMap]);

    const handleDrawComplete = useCallback((pts) => {
        setDrawMode(false);
        const hits = cityNbrs.filter(n => { const c = getCentroid(n.boundary); return c && pointInPolygon(c, pts); });
        if (!hits.length) return;
        setSelectedMap(prev => { const next = new Map(prev); hits.forEach(n => next.set(n.id, n)); return next; });
        setLastDrawCount(hits.length);
        setTimeout(() => setLastDrawCount(0), 3000);
    }, [cityNbrs, setSelectedMap]);

    const visibleList = cityNbrs.filter(n =>
        (!selRegion || String(n.region) === String(selRegion)) &&
        (!dSearch.trim() || n.name.includes(dSearch.trim()))
    );
    const selectedIds = new Set(selectedMap.keys());

    return (
        <div className="s5-layout">
            <div className="s5-panel">
                <div className="prw-field">
                    <label className="prw-label">استان</label>
                    <select className="prw-select" value={selProvince} onChange={e => { setSelProvince(e.target.value); setSelCity(''); setCities([]); }}>
                        <option value="">انتخاب استان</option>
                        {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
                <div className="prw-field">
                    <label className="prw-label">شهر</label>
                    <select className="prw-select" value={selCity} onChange={e => setSelCity(e.target.value)} disabled={!selProvince}>
                        <option value="">انتخاب شهر</option>
                        {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="prw-field">
                    <label className="prw-label">منطقه <span style={{ fontWeight: 400, color: '#90a4ae', fontSize: '0.78rem' }}>(اختیاری)</span></label>
                    <select className="prw-select" value={selRegion} onChange={e => setSelRegion(e.target.value)} disabled={!selCity || !regions.length}>
                        <option value="">همه مناطق</option>
                        {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </div>
                {cityNbrs.length > 0 && <input className="prw-input" placeholder="فیلتر نام محله..." value={search} onChange={e => setSearch(e.target.value)} />}
                {loadingNbrs && <p className="s5-searching">در حال بارگذاری...</p>}
                {visibleList.length > 0 && (
                    <ul className="s5-list">
                        {visibleList.map(n => (
                            <li key={n.id} className={`s5-item ${selectedIds.has(n.id) ? 's5-item-sel' : ''}`} onClick={() => toggle(n)}>
                                <span className="s5-check">{selectedIds.has(n.id) ? '✓' : ''}</span>{n.name}
                            </li>
                        ))}
                    </ul>
                )}
                {!selCity && !loadingNbrs && <p className="s5-hint-text">ابتدا استان و شهر را انتخاب کنید.</p>}
                {selectedMap.size > 0 && (
                    <div className="s5-chips">
                        {[...selectedMap.values()].map(n => (
                            <span key={n.id} className="s5-chip">{n.name}
                                <button type="button" onClick={() => toggle(n)} aria-label="حذف">✕</button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="s5-map-wrap">
                {cityNbrs.length > 0 && (
                    <div className="s5-map-toolbar">
                        <button type="button" className={`s5-draw-btn ${drawMode ? 's5-draw-btn-active' : ''}`} onClick={() => setDrawMode(m => !m)}>
                            {drawMode
                                ? <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>لغو رسم</>
                                : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>رسم محدوده</>
                            }
                        </button>
                        {lastDrawCount > 0 && <span className="s5-draw-feedback">✓ {lastDrawCount} محله انتخاب شد</span>}
                    </div>
                )}
                <MapContainer center={[35.6892, 51.389]} zoom={11} className="s5-map">
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                    <NeighborhoodLayer neighborhoods={cityNbrs} selectedIds={selectedIds} onToggle={toggle} drawMode={drawMode} />
                    <DrawLayer drawMode={drawMode} onDrawComplete={handleDrawComplete} />
                    <FlyTo target={flyTarget} />
                </MapContainer>
                <p className="s5-map-hint">
                    {!selCity ? 'برای نمایش محله‌ها روی نقشه، یک شهر را انتخاب کنید.'
                        : loadingNbrs ? 'در حال بارگذاری...'
                        : drawMode ? '🖊 موس را نگه دارید و محدوده را رسم کنید.'
                        : `${cityNbrs.length} محله بارگذاری شد`}
                </p>
            </div>
        </div>
    );
}
